import { useState, useCallback, useEffect, useRef } from 'react';
import type { Musician, ChannelConflict } from '../types';
import { createMusician } from '../types';
import { useProfileContext } from './useProfileContext';
import { useRealtime } from './useRealtime';
import { apiGetMusiciansMeta, apiSaveMusicians, ApiError } from '../api';
import { reportSaveError, reportSaveOk, isShowcaseMode } from '../lib/saveStatus';
import { pushConfigNotice, CONFIG_CONFLICT_MESSAGE } from '../lib/configGuard';

export function useMusicians(bandId?: string) {
  const { activeProfileId } = useProfileContext();
  const { refreshToken } = useRealtime(bandId);
  const effectiveId = bandId || activeProfileId;

  const [musicians, setMusicians] = useState<Musician[]>([]);
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipSave = useRef(true);
  // Anti-loop guard: snapshot of the last persisted/fetched state, so a
  // real-time refetch does not write the freshly-received data straight back.
  const lastSavedRef = useRef<string | null>(null);
  // Anti data-loss (v0.650): the `rider_musicians.last_modified` this session
  // loaded, echoed on the PUT (`X-Rider-Version`) so the server can refuse a
  // stale write (409) instead of clobbering a fresher roster.
  const versionRef = useRef<number | null>(null);
  // Stable handle to `reload` (defined below) for the save error path (409) to
  // refetch the latest list without a TDZ / dependency cycle.
  const reloadRef = useRef<() => Promise<void>>(async () => {});

  // --- Load musicians from server when profile changes ---
  useEffect(() => {
    if (!effectiveId) {
      setMusicians([]);
      setLoaded(true);
      skipSave.current = true;
      versionRef.current = null;
      return;
    }

    let cancelled = false;
    skipSave.current = true;
    setLoaded(false);

    (async () => {
      try {
        const { data, version } = await apiGetMusiciansMeta(effectiveId);
        if (!cancelled) {
          versionRef.current = version;
          lastSavedRef.current = JSON.stringify(data || []);
          setMusicians(data || []);
          setLoaded(true);
          skipSave.current = false;
        }
      } catch (err) {
        console.error('[useMusicians] Failed to load from API:', err);
        // SECURITY: on load failure, NEVER overwrite server data with [].
        // Keep previous state and stay in skipSave mode so the debounced
        // save cannot push an empty array and wipe the band's musicians.
        if (!cancelled) {
          skipSave.current = true;
          reportSaveError('musicians', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveId]);

  // --- Debounced save to server ---
  useEffect(() => {
    if (skipSave.current || !effectiveId || !loaded) return;
    // Showcase (demo) account: never autosave. (2026-09-15)
    if (isShowcaseMode()) return;

    // Anti-loop guard: skip when nothing changed since the last persist/fetch.
    if (JSON.stringify(musicians) === lastSavedRef.current) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(() => {
      // Snapshot the version loaded; the server refuses a stale write (409).
      const sentVersion = versionRef.current;
      apiSaveMusicians(effectiveId, musicians, { version: sentVersion })
        .then((res) => {
          lastSavedRef.current = JSON.stringify(musicians);
          if (typeof res.version === 'number') versionRef.current = res.version;
          reportSaveOk('musicians');
        })
        .catch((err) => {
          // CONCURRENCY GUARD: another session wrote after we loaded. The server
          // wrote NOTHING. Warn visibly and reload the latest list.
          if (err instanceof ApiError && err.status === 409 && (err.body as { error?: string })?.error === 'stale') {
            pushConfigNotice('conflict', CONFIG_CONFLICT_MESSAGE);
            void reloadRef.current().catch(() => undefined);
            return;
          }
          console.error('[useMusicians] Failed to save to API:', err);
          reportSaveError('musicians', err);
        });
    }, 400);

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [musicians, effectiveId, loaded]);

  const saveMusiciansToServer = useCallback((toSave: Musician[]) => {
    if (!effectiveId) return Promise.resolve();
    if (saveTimer.current) clearTimeout(saveTimer.current);
    return apiSaveMusicians(effectiveId, toSave, { version: versionRef.current })
      .then((res) => {
        lastSavedRef.current = JSON.stringify(toSave);
        if (typeof res.version === 'number') versionRef.current = res.version;
        reportSaveOk('musicians');
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 409 && (err.body as { error?: string })?.error === 'stale') {
          pushConfigNotice('conflict', CONFIG_CONFLICT_MESSAGE);
          void reloadRef.current().catch(() => undefined);
          return;
        }
        console.error('[useMusicians] Failed to save to API:', err);
        reportSaveError('musicians', err);
      });
  }, [effectiveId]);

  // Refetch from the server, keeping the SECURITY guard (never wipe on error).
  const reload = useCallback(async () => {
    if (!effectiveId) return;
    try {
      const { data, version } = await apiGetMusiciansMeta(effectiveId);
      versionRef.current = version;
      lastSavedRef.current = JSON.stringify(data || []);
      setMusicians(data || []);
      setLoaded(true);
      skipSave.current = false;
      reportSaveOk('musicians');
    } catch (err) {
      console.error('[useMusicians] Failed to reload from API:', err);
      skipSave.current = true;
      reportSaveError('musicians', err);
      throw err;
    }
  }, [effectiveId]);

  reloadRef.current = reload;

  // Real-time: refetch when another client updates this band (anti-loop guard
  // above + server-side self-broadcast suppression prevent a write-back loop).
  useEffect(() => {
    if (refreshToken > 0 && effectiveId) {
      void reload().catch(() => undefined);
    }
  }, [refreshToken, effectiveId, reload]);

  const addMusician = useCallback(
    (data?: Partial<Musician>) => {
      const m = createMusician(data, musicians.length);
      const updated = [...musicians, m];
      setMusicians(updated);
      return { musician: m, updated };
    },
    [musicians],
  );

  const updateMusician = useCallback((id: string, data: Partial<Musician>) => {
    setMusicians((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...data } : m)),
    );
  }, []);

  const deleteMusician = useCallback((id: string) => {
    setMusicians((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const reorderMusicians = useCallback((orderedIds: string[]) => {
    setMusicians((prev) => {
      const map = new Map(prev.map((m) => [m.id, m]));
      return orderedIds
        .map((id, i) => {
          const m = map.get(id);
          if (!m) return null;
          return { ...m, order: i };
        })
        .filter((m): m is Musician => m !== null);
    });
  }, []);

  const getMusician = useCallback(
    (id: string) => musicians.find((m) => m.id === id) ?? null,
    [musicians],
  );

  const conflicts = useCallback((): ChannelConflict[] => {
    const result: ChannelConflict[] = [];
    const channelMap = new Map<
      number,
      { musicianName: string; instrumentName: string }[]
    >();

    for (const m of musicians) {
      for (const inst of m.instruments) {
        if (!inst.channel || inst.channel <= 0) continue;
        const entry = channelMap.get(inst.channel) ?? [];
        entry.push({
          musicianName: m.name || 'Unnamed',
          instrumentName: inst.name || 'Unnamed',
        });
        channelMap.set(inst.channel, entry);
      }
    }

    for (const [channel, entries] of channelMap) {
      if (entries.length > 1) {
        for (let i = 0; i < entries.length; i++) {
          for (let j = i + 1; j < entries.length; j++) {
            result.push({
              channel,
              musician1Name: entries[i].musicianName,
              musician2Name: entries[j].musicianName,
              instrument1Name: entries[i].instrumentName,
              instrument2Name: entries[j].instrumentName,
            });
          }
        }
      }
    }

    return result;
  }, [musicians]);

  return {
    musicians,
    addMusician,
    updateMusician,
    deleteMusician,
    reorderMusicians,
    getMusician,
    conflicts,
    saveMusiciansToServer,
    // Refetch from the server (keeps the anti-wipe guard). Exposed so a page can
    // refresh after a SERVER-side mutation of the rider array — e.g. the
    // explicit kind move (musician ↔ crew). (2026-10-12)
    reload,
  };
}